import { useState } from "react";
import { X, FileText, Save } from "lucide-react";
import { api } from "../../lib/api";
import {
  LeadRowActions,
  NewPill,
  isRecordNew,
  unreadRowClass,
  markRead,
} from "./LeadRowActions";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "appointment_scheduled", label: "Appointment Scheduled" },
  { value: "follow_up_needed", label: "Follow-Up Needed" },
  { value: "closed", label: "Closed" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

const ConsultationsManager = ({ consultations, refresh }) => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="bg-white">
      {consultations.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">No insurance consultations yet</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {consultations.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                if (isRecordNew(c)) markRead("insurance-consultations", c.id).then(refresh);
                setSelected(c);
              }}
              data-testid={`admin-consultation-row-${c.id}`}
              className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${unreadRowClass(c)}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className={`text-slate-900 ${isRecordNew(c) ? "font-bold" : "font-medium"}`}>
                      {c.firstName} {c.lastName}
                    </h3>
                    {isRecordNew(c) && <NewPill />}
                    <StatusPill status={c.status} />
                  </div>
                  <p className="text-slate-500 text-sm">
                    {c.email} · {c.phone}
                  </p>
                  <p className="text-slate-400 text-xs mt-1 font-mono">{c.referenceNumber}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-right">
                    <span className="text-xs text-slate-400">{formatDate(c.created_at)}</span>
                    <p className="text-sm text-[#C89B3C] mt-1">
                      {c.desiredCoverage || "Coverage TBD"}
                    </p>
                  </div>
                  <LeadRowActions
                    collection="insurance-consultations"
                    recordId={c.id}
                    isRead={c.isRead !== false}
                    onChanged={refresh}
                  />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <ConsultationDetail
          consultation={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            refresh();
            setSelected(null);
          }}
        />
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const s = STATUSES.find((x) => x.value === status) || STATUSES[0];
  const colors = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    contacted: "bg-amber-50 text-amber-700 border-amber-200",
    appointment_scheduled: "bg-emerald-50 text-emerald-700 border-emerald-200",
    follow_up_needed: "bg-purple-50 text-purple-700 border-purple-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${colors[s.value]}`}>
      {s.label}
    </span>
  );
};

const ConsultationDetail = ({ consultation: c, onClose, onSaved }) => {
  const [status, setStatus] = useState(c.status || "new");
  const [notes, setNotes] = useState(c.internalNotes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/insurance-consultation/${c.id}`, {
        status,
        internalNotes: notes,
      });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-consultation-detail"
      >
        <div className="bg-[#0B2343] p-6 sticky top-0 z-10">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[#C89B3C] font-mono text-xs mb-1">{c.referenceNumber}</p>
              <h2 className="font-serif text-xl font-bold text-white">
                {c.firstName} {c.middleName || ""} {c.lastName} {c.suffix || ""}
              </h2>
              <p className="text-slate-300 text-sm">
                {c.desiredCoverage} · {c.monthlyBudget}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white"
              data-testid="admin-consultation-close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status + notes admin controls */}
          <div className="bg-slate-50 border border-slate-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="admin-consultation-status"
                  className="w-full border border-slate-200 px-3 py-2 text-sm"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">
                  Submitted
                </label>
                <p className="text-sm py-2">{formatDate(c.created_at)}</p>
              </div>
            </div>
            <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">
              Internal notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="admin-consultation-notes"
              placeholder="Add notes visible only to agents…"
              className="w-full border border-slate-200 px-3 py-2 text-sm"
            />
            <div className="mt-3 text-right">
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-consultation-save"
                className="bg-[#0B2343] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>

          <Section title="Contact">
            <Grid2>
              <Item label="Email">
                <a href={`mailto:${c.email}`} className="text-[#C89B3C]">{c.email}</a>
              </Item>
              <Item label="Phone">
                <a href={`tel:${c.phone}`} className="text-[#C89B3C]">{c.phone}</a>
              </Item>
              <Item label="ZIP">{c.zipCode}</Item>
              <Item label="Preferred contact">{c.preferredContact} ({c.bestTimeToContact})</Item>
              <Item label="Language">
                {c.preferredLanguage}{c.preferredLanguageOther ? ` — ${c.preferredLanguageOther}` : ""}
              </Item>
              <Item label="Heard about us">
                {c.hearAboutUs}{c.referrerName ? ` — ${c.referrerName}` : ""}
                {c.hearAboutUsOther ? ` — ${c.hearAboutUsOther}` : ""}
              </Item>
              <Item label="Advisor referral">
                {c.referredByAdvisor}{c.advisorName ? ` — ${c.advisorName}` : ""}
              </Item>
            </Grid2>
          </Section>

          <Section title="About the applicant">
            <Grid2>
              <Item label="Date of birth">{c.dateOfBirth}</Item>
              <Item label="Marital status">{c.maritalStatus}</Item>
              <Item label="Height">{c.heightFeet}′ {c.heightInches}″</Item>
              <Item label="Weight">{c.weightPounds} lbs</Item>
              <Item label="Occupation">{c.occupation}</Item>
              <Item label="Employment">
                {c.employmentStatus}{c.employmentStatusOther ? ` — ${c.employmentStatusOther}` : ""}
              </Item>
              <Item label="Household income">{c.annualIncome}</Item>
              <Item label="Net worth">{c.netWorth}</Item>
            </Grid2>
          </Section>

          <Section title="Tobacco / nicotine / cannabis">
            <Grid2>
              <Item label="Products used">{(c.tobaccoUse || []).join(", ") || "—"}</Item>
              <Item label="Frequency">{c.tobaccoFrequency || "—"}</Item>
              <Item label="Last used">{c.tobaccoLastUsed || "—"}</Item>
            </Grid2>
          </Section>

          <Section title="Goals & desired coverage">
            <Grid2>
              <Item label="Goals">{(c.insuranceGoals || []).join(", ") || "—"}</Item>
              <Item label="Goals — other">{c.insuranceGoalsOther || "—"}</Item>
              <Item label="Desired coverage">{c.desiredCoverage}</Item>
              <Item label="Monthly budget">{c.monthlyBudget}</Item>
            </Grid2>
          </Section>

          <Section title="Current coverage">
            <Grid2>
              <Item label="Has coverage">{c.currentCoverage}</Item>
              <Item label="Company">{c.currentCompany || "—"}</Item>
              <Item label="Amount">{c.currentCoverageAmount || "—"}</Item>
              <Item label="Premium">{c.currentPremium || "—"}</Item>
              <Item label="Review existing">{c.reviewExistingPolicy || "—"}</Item>
            </Grid2>
          </Section>

          <Section title="Family & dependents">
            <Grid2>
              <Item label="Has dependents">{c.hasDependents}</Item>
              <Item label="Dependent types">
                {(c.dependentTypes || []).join(", ") || "—"}
                {c.dependentTypesOther ? ` — ${c.dependentTypesOther}` : ""}
              </Item>
              <Item label="Number">{c.numberOfDependents || "—"}</Item>
            </Grid2>
          </Section>

          <Section title="Health (PHI — internal only)">
            <Grid2>
              <Item label="Conditions">{(c.healthConditions || []).join(", ") || "—"}</Item>
              <Item label="Conditions detail">{c.healthConditionsDetail || "—"}</Item>
              <Item label="Medications">{c.takingMedications}</Item>
              <Item label="Medications detail">{c.medicationsDetail || "—"}</Item>
              <Item label="Prior decline">{c.priorInsuranceDecline}</Item>
              <Item label="Prior decline detail">{c.priorInsuranceDeclineDetail || "—"}</Item>
            </Grid2>
          </Section>

          <Section title="Background">
            <Grid2>
              <Item label="Military service">{c.militaryService}</Item>
              <Item label="Branch">{c.militaryBranch || "—"}</Item>
              <Item label="Business owner">{c.businessOwner}</Item>
              <Item label="Business protection">
                {(c.businessProtection || []).join(", ") || "—"}
              </Item>
            </Grid2>
          </Section>

          {c.finalComments && (
            <Section title="Final comments">
              <p className="text-sm bg-slate-50 p-3">{c.finalComments}</p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-bold text-slate-900 border-b border-[#C89B3C] pb-2 mb-3">{title}</h3>
    {children}
  </div>
);

const Grid2 = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">{children}</div>
);

const Item = ({ label, children }) => (
  <div>
    <span className="text-slate-500">{label}:</span>
    <br />
    <span className="text-[#0B2343] break-words">{children}</span>
  </div>
);

export default ConsultationsManager;
